import { formatCellValue, getCellValue } from "./columnValues";
import type { DataTableColDef } from "./types";

function toMatrix<R>(columns: DataTableColDef<R>[], rows: R[]): string[][] {
  return [
    columns.map((column) => column.headerName ?? column.field),
    ...rows.map((row) =>
      columns.map((column) =>
        formatCellValue(column, getCellValue(column, row), row),
      ),
    ),
  ];
}

export function buildCsv<R>(columns: DataTableColDef<R>[], rows: R[]): string {
  return toMatrix(columns, rows)
    .map((line) =>
      line.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(","),
    )
    .join("\r\n");
}

export function exportCsv<R>(
  columns: DataTableColDef<R>[],
  rows: R[],
  fileName?: string,
): void {
  const blob = new Blob([buildCsv(columns, rows)], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${fileName || document.title || "export"}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export function printTable<R>(columns: DataTableColDef<R>[], rows: R[]): void {
  const [header, ...body] = toMatrix(columns, rows);
  const printWindow = window.open("", "_blank");
  if (!printWindow) return;
  const cells = (line: string[], tag: string) =>
    line.map((cell) => `<${tag}>${escapeHtml(cell)}</${tag}>`).join("");
  printWindow.document.write(
    `<html><head><title>${escapeHtml(document.title)}</title>` +
      `<style>table{border-collapse:collapse;font-family:sans-serif;font-size:12px}` +
      `th,td{border:1px solid #ddd;padding:4px 8px;text-align:left}</style></head><body>` +
      `<table><thead><tr>${cells(header, "th")}</tr></thead><tbody>${body
        .map((line) => `<tr>${cells(line, "td")}</tr>`)
        .join("")}</tbody></table></body></html>`,
  );
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
}
